import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogImage?: string;
  keywords?: string;
  jsonLd?: Record<string, any> | Record<string, any>[];
}

const BASE_URL = 'https://xarothi.in';
const DEFAULT_OG_IMAGE = `${BASE_URL}/logo512.png`;

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonicalUrl,
  ogImage = DEFAULT_OG_IMAGE,
  keywords,
  jsonLd,
}) => {
  const fullTitle = `${title} | Xarothi`;
  // Match the host's trailing-slash redirect so canonical and og:url point to the served URL.
  const normalizedCanonical =
    canonicalUrl && !/[?#]/.test(canonicalUrl) && !canonicalUrl.endsWith('/')
      ? `${canonicalUrl}/`
      : canonicalUrl;
  const jsonLdArray = jsonLd
    ? Array.isArray(jsonLd) ? jsonLd : [jsonLd]
    : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {normalizedCanonical && <link rel="canonical" href={normalizedCanonical} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Xarothi" />
      {normalizedCanonical && <meta property="og:url" content={normalizedCanonical} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Structured Data */}
      {jsonLdArray.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};
